import { useState, useEffect, useRef } from 'react';
import { calculateApneaRisk, parseUrlParameters } from '../utils/riskCalculator';
import { trackQuizResults, getTrackingParamsFromURL } from '../utils/tracking';
import { useStatsigClient } from '@statsig/react-bindings';

// Apnea Quiz User Segments (converted from Python Enum)
function getApneaQuizUserSegment(apneaRisk) {
  const segments = {
    LOW_RISK: "AQ_A",
    MODERATE_RISK: "AQ_B",
    HIGH_RISK: "AQ_C",
    UNKNOWN: "AQ_D"
  };

  // Convert apnea risk string to UserSegment enum value
  switch(apneaRisk) {
    case "low":
      return segments.LOW_RISK;
    case "moderate":
      return segments.MODERATE_RISK;
    case "high":
      return segments.HIGH_RISK;
    default:
      return segments.UNKNOWN;
  }
}

// Helper function to process medicId and determine referral source
function processMedicId(medicId) {
  if (!medicId) {
    return {
      isBunoReferral: true,
      doctorName: null,
      rawMedicId: null
    };
  }

  const decodedMedicId = decodeURIComponent(medicId);
  const isBuno = decodedMedicId.toLowerCase() === 'buno';

  return {
    isBunoReferral: isBuno,
    doctorName: isBuno ? null : decodedMedicId,
    rawMedicId: medicId
  };
}

export function useRiskCalculation() {
  console.log("=== useRiskCalculation hook initialized ===");
  
  const [riskData, setRiskData] = useState({
    level: 8, // Default fallback
    apneaLikelihood: "high",
    riskNameSpanish: "alto",
    isCalculated: false,
    hasUrlParams: false,
    error: null
  });
  
  console.log("Initial risk data state:", riskData);
  
  // Get Statsig client for tracking
  const { client: statsigClient } = useStatsigClient();
  console.log("Statsig client:", statsigClient ? "available" : "not available");
  
  // Prevent double tracking in development (StrictMode)
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    console.log("=== useRiskCalculation useEffect triggered ===");
    
    const urlParams = parseUrlParameters();
    console.log("Parsed URL parameters:", Object.fromEntries(urlParams.entries()));
    
    // Check if we have any relevant parameters
    const hasRelevantParams = urlParams.has("sbd") || 
      (urlParams.has("w") && urlParams.has("h") && urlParams.has("a") && 
       urlParams.has("nB") && urlParams.has("s") && urlParams.has("ss") && urlParams.has("md"));

    console.log("Has relevant params:", hasRelevantParams);
    console.log("Parameter check breakdown:");
    console.log("  - sbd:", urlParams.has("sbd"));
    console.log("  - w:", urlParams.has("w"));
    console.log("  - h:", urlParams.has("h"));
    console.log("  - a:", urlParams.has("a"));
    console.log("  - nB:", urlParams.has("nB"));
    console.log("  - s:", urlParams.has("s"));
    console.log("  - ss:", urlParams.has("ss"));
    console.log("  - md:", urlParams.has("md"));

    if (hasRelevantParams) {
      console.log("Processing URL parameters for risk calculation");
      
      const result = calculateApneaRisk(urlParams);
      console.log("Risk calculation result:", result);

      const medicInfo = processMedicId(result.medicId);
      console.log("Processed medicId:", medicInfo);

      const newRiskData = {
        level: result.level,
        apneaLikelihood: result.apneaLikelihood,
        riskNameSpanish: result.riskNameSpanish,
        isCalculated: result.success,
        hasUrlParams: true,
        error: result.error,
        medicId: result.medicId,
        isBunoReferral: medicInfo.isBunoReferral,
        doctorName: medicInfo.doctorName
      };
      
      console.log("Setting new risk data:", newRiskData);
      setRiskData(newRiskData);

      // Dispatch custom event for compatibility with existing tracking
      if (result.success) {
        console.log("Dispatching resultsReady event");
        const eventDetail = {
          'apneaLikelyhood': result.apneaLikelihood, // Keep original spelling for compatibility
          'newStopBangDecimal': result.level,
        };
        console.log("Event detail:", eventDetail);
        
        window.dispatchEvent(new CustomEvent("resultsReady", { 
          detail: eventDetail
        }));

        // Enhanced tracking with improved match quality
        try {
          console.log("Starting tracking process");
          
          const trackingData = getTrackingParamsFromURL();
          console.log("Tracking data from URL:", trackingData);
          
          // Get email from URL params if available
          let userEmail = urlParams.get('email') || null;
          let userPhone = urlParams.get('phone') || null;
          
          console.log("Raw email from URL:", userEmail);
          console.log("Raw phone from URL:", userPhone);

          userEmail = userEmail && !userEmail.includes('@') ? null : userEmail;
          userPhone = userPhone && !userPhone.includes('+') ? null : userPhone;
          
          console.log("Processed email:", userEmail);
          console.log("Processed phone:", userPhone);
          
          // Initialize Facebook Pixel with enhanced user data (still needed for pixel init)
          if (typeof window.fbq === 'function') {
            console.log("Facebook Pixel available, initializing");
            
            const fbqInitData = {};
            
            // Add email if available
            if (userEmail) {
              fbqInitData.em = userEmail;
            }

            if (userPhone) {
              fbqInitData.ph = userPhone;
            }

            // Add Facebook browser/click IDs if available
            if (trackingData.fbp) fbqInitData.fbp = trackingData.fbp;
            if (trackingData.fbc) fbqInitData.fbc = trackingData.fbc;

            console.log("Facebook Pixel init data:", fbqInitData);
            window.fbq('init', '1650477155568779', fbqInitData);
          } else {
            console.log("Facebook Pixel not available");
          }
          
          // Use centralized tracking for quiz results (prevent double tracking)
          if (!hasTrackedRef.current && statsigClient) {
            console.log("Tracking quiz results (first time)");
            hasTrackedRef.current = true;
            
            const eventID = urlParams.get('eid') || null;
            console.log("Event ID:", eventID);
            
            const userSegment = getApneaQuizUserSegment(result.apneaLikelihood);
            console.log("User segment:", userSegment);
            
            const trackingPayload = {
              user_segment: userSegment,
            };
            console.log("Tracking payload:", trackingPayload);
            
            trackQuizResults(trackingPayload, eventID, statsigClient);
          } else if (hasTrackedRef.current) {
            console.log("Skipping tracking - already tracked");
          } else {
            console.log("Skipping tracking - no statsig client");
          }
        } catch (trackingError) {
          console.error('=== Tracking error ===');
          console.error('Tracking error:', trackingError);
          console.error('Tracking error message:', trackingError.message);
        }
      } else {
        console.log("Risk calculation failed, not dispatching events or tracking");
      }
    } else {
      console.log("No relevant URL parameters found, using defaults");
      
      // No URL parameters, use defaults
      const defaultRiskData = {
        level: 8, // Default fallback
        apneaLikelihood: "high",
        riskNameSpanish: "alto",
        isCalculated: false,
        hasUrlParams: false,
        error: null
      };
      
      console.log("Setting default risk data:", defaultRiskData);
      setRiskData(defaultRiskData);
    }
    
    console.log("=== useRiskCalculation useEffect completed ===");
  }, [statsigClient]);

  console.log("=== useRiskCalculation hook returning ===");
  console.log("Final risk data:", riskData);
  
  return riskData;
} 